import { FC, useEffect, useRef, useState } from 'react'

import { useAnalytics, useFbp, usePinterest, useRedirect, useSnapChat, useTtq } from '@ui/hooks'
import css from 'styled-jsx/css'
import { REMOVE_ALL_AND_ADD, useNotiContext } from './Notification'
import {
  genSid,
  useCheckoutService,
  useLocalIsBuyNow,
  useLocalIsPaypalThankyou,
  useLocalIsPuchase,
  useLocalSessionId,
  useLocalShippingAddress,
  useOrderService,
  useRefDep
} from '@libs/client'
import { cartMapping } from '@ui/helpers'
import { useGtag } from '@ui/hooks/useGtag'
import getLogRocket from '@ui/analytics/logRocket'
import { logger } from '@ui/analytics'
import { CustomerAddress } from '@libs/client/order/services'
import { method_type } from '@libs/common/models/constant'

interface IPaypalButton {
  paypalEx: S3Types.paygate
  isBuyNow?: boolean
  isPurchase?: boolean
}

const PaypalButton: FC<IPaypalButton> = ({ paypalEx, isBuyNow, isPurchase = true }) => {
  const redirect = useRedirect()
  const { notiDispatch } = useNotiContext()
  const { initPaypal, capturePaypal, cancelPaypal, errorPaypal } = useCheckoutService({
    cartMapping: (cart) => cartMapping(cart)
  })
  const { updateTaxAmount } = useOrderService()
  const [localShipping] = useLocalShippingAddress()

  const [, setLocalSissionId] = useLocalSessionId()
  const {
    fbpTrackInitiateCheckout,
    fbpTrackAddPaymentInfo,
    fbpTrackPurchase,
    fbpInitAdvanceMatching
  } = useFbp()
  const { pinterestTrackPurchase } = usePinterest()
  const {
    snapchatTrackInitiateCheckout,
    snapchatTrackAddPaymentInfo,
    snapchatTrackPurchase
  } = useSnapChat()
  const {
    ttqTrackInitiateCheckout,
    ttqTrackCheckout,
    ttqTrackAddBilling,
    ttqTrackPurchase,
    ttqTrackIdentify
  } = useTtq()
  const {
    gtagTrackBeginCheckout,
    gtagTrackAddPaymentInfo,
    gtagTrackAddShippingInfo,
    gtagTrackPurchase,
    gtagTrackConversionPurchase,
    gtagEnhanceConversion
  } = useGtag()
  const [, setLocalIsPuchase] = useLocalIsPuchase()
  const analytics = useAnalytics()
  const analyticsRef = useRefDep(analytics)
  const localShippingRef = useRefDep(localShipping)
  const currentOrder = useRef<Types.Order | null>(null)
  const [localIsBuyNow] = useLocalIsBuyNow()
  const localIsBuyNowRef = useRefDep(localIsBuyNow)
  const isBuyNowCurrent =
    (typeof isBuyNow === 'boolean' ? isBuyNow : localIsBuyNowRef.current) || false
  const [isLoading, setIsLoading] = useState(true)

  const { credential_clientid } = paypalEx
  const [, setLocalIsPaypalThankyou] = useLocalIsPaypalThankyou()

  useEffect(() => {
    if (!(window as any).paypal) {
      initPaypalScript()
    }

    const interval = setInterval(() => {
      if ((window as any).paypal) {
        setIsLoading(false)
        // @ts-ignore
        window.paypal
          .Buttons({
            style: style,
            createOrder: createOrder,
            onApprove: onApprove,
            onShippingChange: onShippingChange,
            onCancel: onCancel,
            onError: onError
          })
          .render('.js-paypal-button')
          .catch(console.warn)
        clearInterval(interval)
      } else {
        initPaypalScript()
      }
    }, 300)
    return () => {
      clearInterval(interval)
    }
  }, [])

  function initPaypalScript() {
    const PAYPAL_SCRIPT = `https://www.paypal.com/sdk/js?client-id=${credential_clientid}&disable-funding=card,paylater`
    const script = document.createElement('script')
    script.setAttribute('src', PAYPAL_SCRIPT)
    document.head.appendChild(script)
  }

  async function createOrder() {
    getLogRocket().logRocket?.init()
    console.log('createOrder')

    if (!isPurchase) {
      return notiDispatch({
        payload: {
          content: `You cannot checkout. This page is for preview only.`,
          type: 'is-danger'
        },
        type: 'REMOVE_ALL_AND_ADD'
      })
    }

    try {
      // LogRocket.track('PaypalOrderCreated')
      const result = await initPaypal(isBuyNowCurrent) // Important: must be return an order_id for `createOrder` function

      if (result?.status === 'COMPLETED') {
        notiDispatch({
          type: REMOVE_ALL_AND_ADD,
          payload: {
            content: 'Your payment was successfully.',
            type: 'is-success'
          }
        })
        setLocalIsPaypalThankyou(false)
        redirect('/thankyou')
      }
      if (!isBuyNowCurrent) {
        fbpTrackInitiateCheckout(result?.order?.items, result?.order?.items_amount)
        snapchatTrackInitiateCheckout(result?.order?.items, result?.order?.items_amount)
        ttqTrackInitiateCheckout(result?.order?.items, result?.order?.items_amount)
        ttqTrackCheckout(result?.order?.items, result?.order?.items_amount)
        gtagTrackBeginCheckout(result?.order?.items, result?.order?.items_amount)
        logger.logProductEvent('checkout_paypal_cart')
      } else {
        logger.logProductEvent('checkout_paypal_buy_now')
      }

      currentOrder.current = result?.order ?? null

      return result?.orderId
    } catch (err: any) {
      if (err?.message && err?.status) {
        notiDispatch({
          payload: {
            content: err.message || 'Your payment was failed',
            type: 'is-danger'
          },
          type: REMOVE_ALL_AND_ADD
        })
      } else {
        notiDispatch({
          payload: {
            content: err?.response?.data?.message || 'Your payment was failed',
            type: 'is-danger'
          },
          type: REMOVE_ALL_AND_ADD
        })
      }
    }
  }

  async function onApprove(data: any, actions: any) {
    fbpTrackAddPaymentInfo(currentOrder.current?.items, currentOrder.current?.items_amount)
    snapchatTrackAddPaymentInfo(currentOrder.current?.items, currentOrder.current?.items_amount)
    ttqTrackAddBilling(currentOrder.current?.items, currentOrder.current?.items_amount)
    gtagTrackAddPaymentInfo(
      currentOrder.current?.items,
      currentOrder.current?.items_amount,
      'Paypal'
    )
    gtagTrackAddShippingInfo(currentOrder.current?.items, currentOrder.current?.items_amount)

    logger.logProductEvent('paid_paypal')
    try {
      const result = await capturePaypal(data, actions, isBuyNowCurrent)

      notiDispatch({
        type: REMOVE_ALL_AND_ADD,
        payload: {
          content: 'Your payment was successfully.',
          type: 'is-success'
        }
      })

      // Analytics.track('PURCHASE', {
      //   extra: { orderId: this.order.id }
      // })
      // LogRocket.track('Purchased')
      // LogRocket.track('PaypalOrderPurchased')

      fbpTrackPurchase(result?.order.items, result?.order.amount)
      fbpInitAdvanceMatching(result?.order?.shipping)
      pinterestTrackPurchase(result?.order.amount)
      snapchatTrackPurchase(result?.order.items, result?.order.amount)
      ttqTrackIdentify(result?.order?.shipping)
      ttqTrackPurchase(result?.order.items, result?.order.amount)
      gtagTrackPurchase(result?.order)
      gtagTrackConversionPurchase(
        `AW-${analyticsRef?.current?.google_conversion_id || ''}/${
          analyticsRef?.current?.google_conversion_label || ''
        }`,
        result?.order
      )
      gtagEnhanceConversion(result?.order)
      setLocalSissionId(genSid())
      setLocalIsPuchase(true)
      if (!isBuyNowCurrent) {
        logger.logProductEvent('payment_success_paypal_cart')
      } else {
        logger.logProductEvent('payment_success_paypal_buy_now')
      }
      logger.logProductEvent('payment_success')
      setLocalIsPaypalThankyou(false)
      redirect('/thankyou')
    } catch (err: any) {
      if (err?.message && err?.status) {
        notiDispatch({
          payload: {
            content: err.message || 'Your payment was failed',
            type: 'is-danger'
          },
          type: REMOVE_ALL_AND_ADD
        })
      } else {
        notiDispatch({
          payload: {
            content: err?.response?.data?.message || 'Your payment was failed',
            type: 'is-danger'
          },
          type: REMOVE_ALL_AND_ADD
        })
      }
      logger.logProductEvent('payment_failure')
    }
  }

  async function onShippingChange(data: any, actions: any) {
    // Get tax rate for state
    try {
      const order = await updateTaxAmount(
        data?.shipping_address as CustomerAddress,
        method_type.PAYPALEX
      )
      return actions.order.patch([
        {
          op: 'replace',
          path: "/purchase_units/@reference_id=='default'/amount",
          value: {
            currency_code: 'USD',
            value: (order?.amount || 0).toFixed(2),
            breakdown: {
              item_total: {
                currency_code: 'USD',
                value: (order?.items_amount || 0).toFixed(2)
              },
              shipping: {
                currency_code: 'USD',
                value: (order?.shipping_amount || 0).toFixed(2)
              },
              discount: {
                currency_code: 'USD',
                value: (order?.discount_amount || 0).toFixed(2)
              },
              tax_total: {
                currency_code: 'USD',
                value: (order?.tax_amount || 0).toFixed(2)
              }
            }
          }
        }
      ])
    } catch (error) {
      const order = await updateTaxAmount(
        data?.shipping_address as CustomerAddress,
        method_type.PAYPALEX
      )
      return actions.order.patch([
        {
          op: 'replace',
          path: "/purchase_units/@reference_id=='default'/amount",
          value: {
            currency_code: 'USD',
            value: (order?.amount || 0).toFixed(2),
            breakdown: {
              item_total: {
                currency_code: 'USD',
                value: (order?.items_amount || 0).toFixed(2)
              },
              shipping: {
                currency_code: 'USD',
                value: (order?.shipping_amount || 0).toFixed(2)
              },
              discount: {
                currency_code: 'USD',
                value: (order?.discount_amount || 0).toFixed(2)
              },
              tax_total: {
                currency_code: 'USD',
                value: (order?.tax_amount || 0).toFixed(2)
              }
            }
          }
        }
      ])
    }
  }

  function onError(err: Error) {
    console.warn('Error while processing the payment with Paypal', err)
    updateTaxAmount(localShippingRef.current || {}, method_type.CREDITCARD)
    errorPaypal()
  }

  function onCancel(data: any) {
    updateTaxAmount(localShippingRef.current || {}, method_type.CREDITCARD, true)
    cancelPaypal(data, isBuyNowCurrent)
  }

  return (
    <>
      <style jsx>{paypalStyle}</style>
      <div className="paypal-button">
        {isLoading ? (
          <button className="loading-button button is-fullwidth is-warning is-loading mb-1" />
        ) : null}
        <div className="js-paypal-button" />
      </div>
    </>
  )
}

export default PaypalButton

const style = {
  color: 'gold',
  height: 55,
  layout: 'horizontal',
  size: 'medium',
  shape: 'rect',
  tagline: false,
  fundingicons: 'false'
}

const paypalStyle = css`
  .paypal-button {
    .loading-button {
      background-color: #ffc43a !important;
      height: 55px;
    }
  }
`
